/**
 * Shared renderers for the four declarative filter primitives (multiselect,
 * select, toggle, search). Pulled out of `DataViewTable` so the
 * unified `DataViewFilterPanel` can render the controls inside its
 * Filters section while the table still owns the predicate logic.
 *
 * The renderers are pure functions of `(filter, value, onChange)`. State
 * lives wherever the caller wants it (today: in `useExceptionFilterValues`
 * lifted to the route, passed into both panel and table).
 */
import React from 'react'
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import DateRangePickerField from '../../shared/filters/fields/DateRangePickerField'
import type {
  ExceptionFilterDef,
  ExceptionFilterOption,
} from '../../../config/exceptions/types'

/**
 * Empty/default value map for a filters list. Multiselects start empty,
 * selects fall to their declared default (or 'all'), toggles to their
 * declared default (or off), search to ''. Used as the lazy useState
 * initialiser and again by `resetFilters()` so the two stay in sync.
 */
export const buildInitialFilterValues = (
  filters?: ExceptionFilterDef[],
): Record<string, any> => {
  const init: Record<string, any> = {}
  for (const f of filters ?? []) {
    if (f.type === 'multiselect') init[f.key] = []
    else if (f.type === 'select') init[f.key] = f.defaultValue ?? 'all'
    else if (f.type === 'toggle') init[f.key] = !!f.defaultEnabled
    else if (f.type === 'search') init[f.key] = ''
    else if (f.type === 'daterange') init[f.key] = f.defaultValue ?? ['', '']
  }
  return init
}

interface RenderFilterControlArgs {
  filter: ExceptionFilterDef
  value: any
  onChange: (next: any) => void
}

/**
 * Renders one filter control. Search is handled by `renderSearchInput`
 * below so callers that want a different placement for search (e.g. the
 * panel hosts it in its own slot above the dropdowns) don't have to
 * branch on type.
 */
export const renderFilterControl = ({
  filter,
  value,
  onChange,
}: RenderFilterControlArgs): React.ReactNode => {
  if (filter.type === 'multiselect') {
    const options = (filter.options ?? []) as ExceptionFilterOption[]
    const selectedValues = (value ?? []) as string[]
    const selectedOptions = options.filter((o) => selectedValues.includes(o.value))
    return (
      <Autocomplete
        key={filter.key}
        multiple
        size="small"
        options={options}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        value={selectedOptions}
        onChange={(_, vals) => onChange(vals.map((v) => v.value))}
        disableCloseOnSelect
        renderOption={(props, option) => {
          // Two-column row: label on left, count right-aligned as muted
          // secondary text. `variant: 'subtle'` italicises + mutes the label
          // (used for "No category" so it reads as distinct from real names).
          const isSubtle = option.variant === 'subtle'
          return (
            <Box
              component="li"
              {...props}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontStyle: isSubtle ? 'italic' : 'normal',
                  color: isSubtle ? 'text.secondary' : 'text.primary',
                }}
              >
                {option.label}
              </Typography>
              {typeof option.count === 'number' && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {option.count.toLocaleString()}
                </Typography>
              )}
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={filter.label}
            placeholder={selectedOptions.length === 0 ? 'All' : ''}
            InputLabelProps={{ shrink: true }}
          />
        )}
        sx={{ minWidth: 220 }}
      />
    )
  }
  if (filter.type === 'select') {
    const current = (value ?? filter.defaultValue ?? 'all') as string
    const labelId = `filter-${filter.key}-label`
    return (
      <FormControl key={filter.key} size="small" sx={{ minWidth: 160 }}>
        <InputLabel id={labelId} shrink>
          {filter.label}
        </InputLabel>
        <Select
          labelId={labelId}
          label={filter.label}
          notched
          value={current}
          onChange={(e) => onChange(e.target.value)}
        >
          {filter.options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.icon ? (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    {opt.icon}
                  </Box>
                  <span>{opt.label}</span>
                </Stack>
              ) : (
                opt.label
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }
  if (filter.type === 'daterange') {
    const [start, end] = ((value as [string, string] | undefined) ?? ['', '']) as [
      string,
      string,
    ]
    return (
      <Box key={filter.key} sx={{ minWidth: 240, flex: '0 1 280px' }}>
        <DateRangePickerField
          hideLabel
          startDate={start}
          endDate={end}
          onChange={(s, e) => onChange([s, e])}
        />
      </Box>
    )
  }
  if (filter.type === 'toggle') {
    const enabled = !!(value ?? filter.defaultEnabled)
    return (
      <FormControlLabel
        key={filter.key}
        sx={{ ml: 0, mr: 0 }}
        control={
          <Switch
            size="small"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {filter.labelNode ?? filter.label}
          </Typography>
        }
      />
    )
  }
  return null
}

interface RenderSearchInputArgs {
  filter: Extract<ExceptionFilterDef, { type: 'search' }>
  value: string
  onChange: (next: string) => void
}

export const renderSearchInput = ({
  filter,
  value,
  onChange,
}: RenderSearchInputArgs): React.ReactNode => {
  // No floating label — the placeholder carries the "what can be
  // searched" cue (eg. "Search client, sale # or invoice"). The search
  // field sits in the same row as the dropdowns; its leading magnifier
  // icon visually separates it from labelled controls.
  return (
    <TextField
      size="small"
      placeholder={filter.placeholder || filter.label || 'Search'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0.25, mr: 0.5 }}>
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="Clear search"
              size="small"
              edge="end"
              onClick={() => onChange('')}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  )
}

/**
 * Lookup helper used by callers that need the search filter and want to
 * present it in a dedicated slot.
 */
export const findSearchFilter = (
  filters?: ExceptionFilterDef[],
): Extract<ExceptionFilterDef, { type: 'search' }> | undefined =>
  (filters ?? []).find((f): f is Extract<ExceptionFilterDef, { type: 'search' }> =>
    f.type === 'search',
  )
